import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, MessageBar, MessageBarBody, Spinner, Text, tokens } from "@fluentui/react-components";
import { Copy16Regular, Send16Regular } from "@fluentui/react-icons";
import { GENDER_LABEL, ageOn, type CreateClientInput, type Gender } from "@gymplan/shared";
import { api, type AvailabilitySlot, type Client } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { ClientForm } from "../components/ClientForm.tsx";
import { ConfirmButton } from "../components/ConfirmButton.tsx";
import { MessageLog } from "../components/MessageLog.tsx";

export function ClientDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { user } = useAuth();
  const gyms = user?.trainer?.gyms ?? [];
  const q = useQuery({ queryKey: ["client", id], queryFn: () => api.get<Client>(`/api/clients/${id}`), enabled: !!id });
  const avail = useQuery({ queryKey: ["availability"], queryFn: () => api.get<{ slots: AvailabilitySlot[] }>("/api/trainers/me/availability").then((r) => r.slots) });
  const save = useMutation({
    mutationFn: (v: CreateClientInput) => api.patch<Client>(`/api/clients/${id}`, v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      void qc.invalidateQueries({ queryKey: ["client", id] });
    },
  });
  const link = useQuery({ queryKey: ["portal-link", id], queryFn: () => api.get<{ url: string }>(`/api/clients/${id}/portal-link`), enabled: !!id });
  const sendLink = useMutation({ mutationFn: () => api.post<{ url: string; simulated: boolean; suppressed: boolean }>(`/api/clients/${id}/portal-link/send`) });
  const rotateLink = useMutation({ mutationFn: () => api.post<{ url: string }>(`/api/clients/${id}/portal-link/rotate`), onSuccess: () => void qc.invalidateQueries({ queryKey: ["portal-link", id] }) });
  const consent = useQuery({ queryKey: ["consent", id], queryFn: () => api.get<{ processing: { source: string; createdAt: string } | null; whatsapp: { granted: boolean; source: string; createdAt: string } | null }>(`/api/clients/${id}/consent`), enabled: !!id });
  const erase = useMutation({
    mutationFn: () => api.del(`/api/clients/${id}?erase=true`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      nav("/clients");
    },
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/api/clients/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      nav("/clients");
    },
  });

  if (q.isLoading) return <Spinner />;
  if (!q.data) return <Text>Client not found.</Text>;
  const c = q.data;

  return (
    <div style={{ maxWidth: 1000 }}>
      <Text as="h1" size={800} weight="semibold" block>
        {c.name}
      </Text>
      <Text block style={{ color: tokens.colorNeutralForeground3, marginBottom: 16 }}>
        {c.phone}
        {c.dateOfBirth ? ` · ${ageOn(c.dateOfBirth.slice(0, 10))} years old` : ""}
        {c.gender ? ` · ${c.gender === "self_described" ? (c.genderDescription ?? GENDER_LABEL.self_described) : GENDER_LABEL[c.gender as Gender]}` : ""}
        {c.joinedAt ? ` · ${c.askEachWeek === false ? "books usual times, only asked when your hours change" : "checked in every week"}` : ""}
      </Text>
      {!c.joinedAt && (
        <MessageBar intent="info" style={{ marginBottom: 16 }}>
          <MessageBarBody>
            <Badge appearance="tint" color="informative" style={{ marginRight: 8 }}>Invited</Badge>
            {c.name.split(" ")[0]} has had a WhatsApp from RackUp with their own sign-up link{c.inviteRemindedAt ? " and a reminder" : ""}, but hasn't joined yet. Until they do they aren't asked about any week. You can fill in their details here yourself if you'd rather.
          </MessageBarBody>
        </MessageBar>
      )}
      <Card style={{ padding: 16, marginBottom: 20, display: "flex", flexDirection: "column", gap: 8 }}>
        <Text weight="semibold">Personal link</Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          {c.name.split(" ")[0]} can open this to change their own times and details — no password needed.
        </Text>
        <Text size={200} style={{ wordBreak: "break-all" }}>{link.data?.url ?? "…"}</Text>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button size="small" icon={<Copy16Regular />} onClick={() => link.data && void navigator.clipboard.writeText(link.data.url)}>Copy</Button>
          <Button size="small" appearance="primary" icon={<Send16Regular />} onClick={() => sendLink.mutate()} disabled={sendLink.isPending}>Send on WhatsApp</Button>
          <ConfirmButton size="small" appearance="subtle" title="Generate a new link?" message="The old link stops working immediately. Send the new one to the client afterwards." confirmLabel="New link" onConfirm={() => rotateLink.mutate()}>New link</ConfirmButton>
          <MessageLog clientId={c.id} clientName={c.name} />
        </div>
        {sendLink.isSuccess && (
          <MessageBar intent={sendLink.data.suppressed ? "warning" : sendLink.data.simulated ? "info" : "success"}>
            <MessageBarBody>{sendLink.data.suppressed ? "Not sent — this client has opted out of WhatsApp messages. Copy the link and send it yourself." : sendLink.data.simulated ? "WhatsApp is in simulation mode — the message was logged, not sent." : "Sent."}</MessageBarBody>
          </MessageBar>
        )}
      </Card>
      <ClientForm
        key={c.id}
        gyms={gyms}
        availability={avail.data}
        sessionLengthMinutes={user?.trainer?.sessionLengthMinutes}
        initial={{
          name: c.name,
          phone: c.phone,
          email: c.email ?? "",
          sessionsPerWeek: c.sessionsPerWeek,
          preferences: c.preferences.map((p) => ({
            weekday: p.weekday as never,
            startTime: p.startTime ?? undefined,
            endTime: p.endTime ?? undefined,
            gymIds: p.gyms.map((g) => g.gym.id),
          })),
        }}
        onSubmit={(v) => save.mutateAsync(v).then(() => undefined)}
        busy={save.isPending}
      />
      {save.isSuccess && <Text style={{ color: tokens.colorPaletteGreenForeground1 }}>Saved.</Text>}
      <Card style={{ padding: 16, marginTop: 24, display: "flex", flexDirection: "column", gap: 8 }}>
        <Text weight="semibold">Consent & data</Text>
        <Text size={200}>
          Processing: {consent.data?.processing ? `${label(consent.data.processing.source)} · ${new Date(consent.data.processing.createdAt).toLocaleDateString("en-GB")}` : "—"}
          {" · "}WhatsApp: {consent.data?.whatsapp ? `${consent.data.whatsapp.granted ? "opted in" : "opted out"} (${label(consent.data.whatsapp.source)})` : "—"}
        </Text>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button as="a" href={`/api/clients/${c.id}/export`} size="small" appearance="secondary">Export their data</Button>
          <ConfirmButton size="small" appearance="outline" title={`Archive ${c.name}?`} message="Stops check-ins and sessions; their record is kept." confirmLabel="Archive" onConfirm={() => remove.mutate()}>Archive</ConfirmButton>
          <ConfirmButton size="small" appearance="outline" title={`Erase ${c.name}'s data?`} message="Permanently removes their details, preferences and messages (use this for a deletion request). Past sessions stay as 'Deleted client'. Can't be undone." confirmLabel="Erase" onConfirm={() => erase.mutate()}>Erase (GDPR)</ConfirmButton>
        </div>
      </Card>
    </div>
  );
}

const label = (src: string) =>
  ({ SELF_SIGNUP: "agreed at sign-up", PORTAL: "set on their page", TRAINER_ADDED: "added by you (not yet confirmed by client)", TRAINER_ATTESTED: "added by you, consent attested", WHATSAPP_REPLY: "by WhatsApp reply" })[src] ?? src;
